import React from 'react';
import PropTypes from 'prop-types';
import FieldConstraintForm from './FieldConstraintForm';
import DanglingRefRow from './DanglingRefRow';

function newFieldConstraint(fieldName, addressable) {
	const constraint = {
		fieldName,
		required: true,
		type: addressable?.dataType || 'string',
	};
	// For array fields, carry the base schema's item type through so
	// validateFieldConstraints doesn't flag a missing arrayItemType.
	if (addressable?.dataType === 'array' && addressable?.arrayItemType) {
		constraint.arrayItemType = addressable.arrayItemType;
	}
	if (addressable?.format) constraint.format = addressable.format;
	return constraint;
}

/**
 * Group addressable entries by top-level parent so the chip grid renders
 * with a visual header per nest. Preserves input order within each group
 * (paths.js already emits containers before children).
 */
function groupByTopLevel(fields) {
	const groups = new Map();
	for (const f of fields) {
		const groupKey = f.topLevel || f.displayPath;
		if (!groups.has(groupKey)) groups.set(groupKey, []);
		groups.get(groupKey).push(f);
	}
	return groups;
}

/**
 * Shared editor for THEN and ELSE field lists. Renders a checkbox column of
 * available top-level fields + per-selected-field `FieldConstraintForm`.
 * Fields referenced by the branch but missing from the current schema render
 * as `DanglingRefRow` instead.
 */
export default function BranchFieldsEditor({
	branch,
	ruleIndex,
	selectedFields,
	availableFields,
	errors,
	onChange,
}) {
	// Any schema field may be constrained in THEN / ELSE — including fields
	// referenced in the IF clause (e.g. when `sss` equals 2, additionally
	// require `sss` to match a pattern is a valid x-allOf pattern).
	const selectable = availableFields;
	const availableNames = new Set(selectable.map((f) => f.displayPath));
	const selectedNames = new Set(selectedFields.map((f) => f.fieldName));
	const groups = groupByTopLevel(selectable);

	const toggle = (displayPath) => {
		if (selectedNames.has(displayPath)) {
			onChange(selectedFields.filter((f) => f.fieldName !== displayPath));
			return;
		}
		const addressable = availableFields.find((f) => f.displayPath === displayPath);
		onChange([...selectedFields, newFieldConstraint(displayPath, addressable)]);
	};

	const updateField = (nextField) => {
		onChange(selectedFields.map((f) =>
			f.fieldName === nextField.fieldName ? nextField : f
		));
	};

	const removeField = (name) => {
		onChange(selectedFields.filter((f) => f.fieldName !== name));
	};

	const replaceField = (oldName, newName) => {
		const addressable = availableFields.find((f) => f.displayPath === newName);
		onChange(selectedFields.map((f) =>
			f.fieldName === oldName
				? { ...newFieldConstraint(newName, addressable), required: f.required }
				: f
		));
	};

	return (
		<div className="dsv-xallof-builder__branch-editor">
			{selectable.length === 0 ? (
				<p className="dsv-xallof-builder__hint">
					Add schema fields above before selecting branch fields.
				</p>
			) : (
				<>
					<p className="dsv-xallof-builder__hint">
						Select fields to apply constraints on when this branch is taken.
					</p>
					<div
						className="dsv-xallof-builder__checkboxes"
						data-testid={`xallof-rule-${ruleIndex}-${branch}-checkboxes`}
					>
						{Array.from(groups.entries()).map(([groupKey, entries]) => (
							<div
								key={`grp-${groupKey}`}
								className="dsv-xallof-builder__chip-group"
							>
								{entries.length > 1 && (
									<span className="dsv-xallof-builder__chip-group-label">
										{groupKey}
									</span>
								)}
								{entries.map((f) => {
									const isSelected = selectedNames.has(f.displayPath);
									return (
										<label
											key={f.displayPath}
											className={`dsv-xallof-builder__chip${isSelected ? ' dsv-xallof-builder__chip--selected' : ''}`}
										>
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => toggle(f.displayPath)}
												data-testid={`xallof-rule-${ruleIndex}-${branch}-${f.displayPath}-checkbox`}
											/>
											<span>{f.displayPath}</span>
										</label>
									);
								})}
							</div>
						))}
					</div>
				</>
			)}

			{selectedFields.length > 0 && (
				<div className="dsv-xallof-builder__field-grid">
					{selectedFields.map((f) => {
						const fieldErrors = errors?.[f.fieldName];
						if (!availableNames.has(f.fieldName)) {
							return (
								<DanglingRefRow
									key={f.fieldName}
									scope={`${branch}-field`}
									ruleIndex={ruleIndex}
									missingName={f.fieldName}
									compatibleFields={selectable}
									onReplace={(newName) => replaceField(f.fieldName, newName)}
									onRemove={() => removeField(f.fieldName)}
								/>
							);
						}
						return (
							<FieldConstraintForm
								key={f.fieldName}
								field={f}
								branch={branch}
								ruleIndex={ruleIndex}
								errors={fieldErrors}
								onChange={updateField}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

BranchFieldsEditor.propTypes = {
	branch: PropTypes.oneOf(['then', 'else']).isRequired,
	ruleIndex: PropTypes.number.isRequired,
	selectedFields: PropTypes.array.isRequired,
	availableFields: PropTypes.array.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
};
